import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './AdvertiserForm.css';

interface AffiliateFormData {
  name: string;
  email: string;
  country: string;
  postback: string;
  status: 'pending' | 'approved' | 'blocked';
  tier: 'standard' | 'premium' | 'sensitive';
  postbackPercentage: string;
  postbackStatus: 'start' | 'pause';
  timezone: string;
  dailyCap: string;
  monthlyCap: string;
  apiKey: string;
  password: string;
}


const EditAffiliate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<AffiliateFormData>({
    name: '',
    email: '',
    country: '',
    postback: '',
    status: 'pending',
    tier: 'standard',
    postbackPercentage: '100',
    postbackStatus: 'start',
    timezone: '',
    dailyCap: '',
    monthlyCap: '',
    apiKey: '',
    password: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // Advertiser approval management
  const [advertisers, setAdvertisers] = useState<any[]>([]);
  const [advertiserStatuses, setAdvertiserStatuses] = useState<Record<number, 'approved' | 'blocked'>>({});
  const [advertiserSearch, setAdvertiserSearch] = useState('');

  useEffect(() => {
    const fetchPublisher = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await apiService.getPublisher(parseInt(id));
        const publisher = response.publisher;

        setFormData({
          name: publisher.name || '',
          email: publisher.email || '',
          country: publisher.country || '',
          postback: publisher.callback_template || '',
          status: publisher.status || 'pending',
          tier: publisher.tier || 'standard',
          postbackPercentage: publisher.postback_percentage?.toString() || '100',
          postbackStatus: publisher.postback_status || 'start',
          timezone: publisher.timezone || '',
          dailyCap: publisher.daily_cap?.toString() || '',
          monthlyCap: publisher.monthly_cap?.toString() || '',
          apiKey: publisher.api_key || '',
          password: '' // Empty by default - leave empty to keep current password
        });

        // Fetch advertisers and their approval status for this publisher
        try {
          const advertisersResponse = await apiService.getAdvertisers();
          const publisherApprovals = await apiService.getPublisherAdvertiserApprovals(parseInt(id));

          const advertiserList = advertisersResponse.advertisers || [];
          setAdvertisers(advertiserList);

          // Build approval status map
          const statusMap: Record<number, 'approved' | 'blocked'> = {};
          advertiserList.forEach((adv: any) => {
            // Default to blocked
            statusMap[adv.id] = 'blocked';
          });

          // Update with actual approval status
          if (publisherApprovals && publisherApprovals.approvals) {
            publisherApprovals.approvals.forEach((approval: any) => {
              statusMap[approval.advertiser_id] = approval.status;
            });
          }

          setAdvertiserStatuses(statusMap);
        } catch (err: any) {
          console.error('Failed to fetch advertiser approvals:', err);
        }
      } catch (err: any) {
        setErrors({ fetch: err.message || 'Failed to fetch publisher' });
      } finally {
        setLoading(false);
      }
    };

    fetchPublisher();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.postback.trim()) {
      newErrors.postback = 'Postback URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.postback)) {
      newErrors.postback = 'Postback URL must be a valid URL';
    }

    if (!formData.timezone.trim()) {
      newErrors.timezone = 'Timezone is required';
    }

    // Password is optional in edit mode, but if provided must be at least 6 characters
    if (formData.password && formData.password.trim().length > 0 && formData.password.trim().length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    if (validateForm()) {
      try {
        setLoading(true);
        const updateData: any = {
          name: formData.name,
          email: formData.email,
          country: formData.country,
          callback_template: formData.postback,
          status: formData.status,
          tier: formData.tier,
          postback_percentage: parseInt(formData.postbackPercentage),
          postback_status: formData.postbackStatus,
          timezone: formData.timezone,
          daily_cap: formData.dailyCap ? parseInt(formData.dailyCap) : null,
          monthly_cap: formData.monthlyCap ? parseInt(formData.monthlyCap) : null
        };

        // Only include password if it's provided
        if (formData.password && formData.password.trim().length > 0) {
          updateData.password = formData.password;
        }

        await apiService.updatePublisher(parseInt(id), updateData);
        alert('Publisher updated successfully!');
        navigate('/affiliates');
      } catch (err: any) {
        setErrors({ submit: err.message || 'Failed to update publisher' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/affiliates');
  };

  const generateApiKey = () => {
    const newApiKey = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    setFormData(prev => ({
      ...prev,
      apiKey: newApiKey
    }));
  };

  const toggleAdvertiserStatus = async (advertiserId: number) => {
    if (!id) return;

    const currentStatus = advertiserStatuses[advertiserId];
    const newStatus = currentStatus === 'approved' ? 'blocked' : 'approved';

    try {
      // Call the appropriate API endpoint
      if (newStatus === 'approved') {
        await apiService.approvePublisherForAdvertiser(advertiserId, parseInt(id));
      } else {
        await apiService.blockPublisherForAdvertiser(advertiserId, parseInt(id));
      }

      // Update local state
      setAdvertiserStatuses(prev => ({
        ...prev,
        [advertiserId]: newStatus
      }));

      alert(`Advertiser ${newStatus === 'approved' ? 'approved' : 'blocked'} successfully!`);
    } catch (err: any) {
      alert(`Failed to update advertiser status: ${err.message}`);
    }
  };

  // Approve all advertisers
  const handleApproveAllAdvertisers = async () => {
    if (!id) return;

    const blockedAdvs = blockedAdvertisers;
    if (blockedAdvs.length === 0) {
      alert('No blocked advertisers to approve');
      return;
    }

    try {
      for (const adv of blockedAdvs) {
        await apiService.approvePublisherForAdvertiser(adv.id, parseInt(id));
      }

      const newStatuses = { ...advertiserStatuses };
      blockedAdvs.forEach(adv => {
        newStatuses[adv.id] = 'approved';
      });
      setAdvertiserStatuses(newStatuses);

      alert(`${blockedAdvs.length} advertisers approved successfully!`);
    } catch (err: any) {
      alert(`Failed to approve all advertisers: ${err.message}`);
    }
  };

  // Block all advertisers
  const handleBlockAllAdvertisers = async () => {
    if (!id) return;

    const approvedAdvs = approvedAdvertisers;
    if (approvedAdvs.length === 0) {
      alert('No approved advertisers to block');
      return;
    }

    try {
      for (const adv of approvedAdvs) {
        await apiService.blockPublisherForAdvertiser(adv.id, parseInt(id));
      }

      const newStatuses = { ...advertiserStatuses };
      approvedAdvs.forEach(adv => {
        newStatuses[adv.id] = 'blocked';
      });
      setAdvertiserStatuses(newStatuses);

      alert(`${approvedAdvs.length} advertisers blocked successfully!`);
    } catch (err: any) {
      alert(`Failed to block all advertisers: ${err.message}`);
    }
  };

  // Filter advertisers based on search
  const filteredAdvertisers = advertisers.filter(advertiser =>
    advertiser.name.toLowerCase().includes(advertiserSearch.toLowerCase()) ||
    advertiser.contact_email.toLowerCase().includes(advertiserSearch.toLowerCase())
  );

  const approvedAdvertisers = filteredAdvertisers.filter(adv => advertiserStatuses[adv.id] === 'approved');
  const blockedAdvertisers = filteredAdvertisers.filter(adv => advertiserStatuses[adv.id] === 'blocked');

  return (
    <div className="advertiser-form-page">
      <div className="page-header">
        <h1>Edit Affiliate - ID: {id}</h1>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="advertiser-form">
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter affiliate name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter email address"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={errors.country ? 'error' : ''}
                >
                  <option value="">Select Country</option>
                  <option value="IN">India</option>
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="HK">Hong Kong</option>
                  <option value="SG">Singapore</option>
                  <option value="CY">Cyprus</option>
                  <option value="UA">Ukraine</option>
                </select>
                {errors.country && <span className="error-message">{errors.country}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="timezone">Timezone *</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className={errors.timezone ? 'error' : ''}
                >
                  <option value="">Select Timezone</option>
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/Toronto">America/Toronto</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                  <option value="Asia/Dubai">Asia/Dubai</option>
                  <option value="Asia/Hong_Kong">Asia/Hong_Kong</option>
                  <option value="Asia/Singapore">Asia/Singapore</option>
                  <option value="Europe/Kiev">Europe/Kiev</option>
                </select>
                {errors.timezone && <span className="error-message">{errors.timezone}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="tier">Publisher Tier</label>
                <select
                  id="tier"
                  name="tier"
                  value={formData.tier}
                  onChange={handleInputChange}
                >
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="sensitive">Sensitive</option>
                </select>
                <small className="field-hint">Tier affects payout calculations</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="postbackPercentage">Postback Percentage</label>
                <select
                  id="postbackPercentage"
                  name="postbackPercentage"
                  value={formData.postbackPercentage}
                  onChange={handleInputChange}
                >
                  <option value="100">100% - All conversions</option>
                  <option value="80">80% - 8 out of 10</option>
                  <option value="60">60% - 6 out of 10</option>
                  <option value="40">40% - 4 out of 10</option>
                  <option value="20">20% - 2 out of 10</option>
                  <option value="10">10% - 1 out of 10</option>
                  <option value="5">5% - 1 out of 20</option>
                </select>
                <small className="field-hint">Controls how many conversion postbacks are fired to publisher</small>
              </div>

              <div className="form-group">
                <label htmlFor="postbackStatus">Postback Hit</label>
                <select
                  id="postbackStatus"
                  name="postbackStatus"
                  value={formData.postbackStatus}
                  onChange={handleInputChange}
                >
                  <option value="start">Start - Fire postbacks</option>
                  <option value="pause">Pause - No postbacks</option>
                </select>
                <small className="field-hint">Control whether postbacks are fired for this publisher</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Login Credentials</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.email}
                  className="form-input"
                  placeholder="Email will be used as username"
                  readOnly
                  style={{ backgroundColor: '#525f6e', cursor: 'not-allowed' }}
                />
                <small className="field-hint">Username is automatically set to the email address</small>
              </div>

              <div className="form-group">
                <label htmlFor="password">New Password (Optional)</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'form-input error' : 'form-input'}
                  placeholder="Leave empty to keep current password"
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
                <small className="field-hint">Enter a new password (min 6 characters) to change it, or leave empty to keep current</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>API Configuration</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="apiKey">API Key</label>
                <div className="api-key-group">
                  <input
                    type="text"
                    id="apiKey"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleInputChange}
                    placeholder="API Key"
                    readOnly
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={generateApiKey}
                  >
                    Generate New
                  </button>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="postback">Postback URL *</label>
                <input
                  type="text"
                  id="postback"
                  name="postback"
                  value={formData.postback}
                  onChange={handleInputChange}
                  className={errors.postback ? 'error' : ''}
                  placeholder="https://example.com/postback?click_id={aff_sub}"
                />
                {errors.postback && <span className="error-message">{errors.postback}</span>}
                <small className="field-hint">
                  Use macros: {'{aff_sub}'} for click ID, {'{payout}'} for payout amount
                </small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Traffic Limits</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dailyCap">Daily Cap</label>
                <input
                  type="number"
                  id="dailyCap"
                  name="dailyCap"
                  value={formData.dailyCap}
                  onChange={handleInputChange}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="field-hint">Maximum clicks per day (0 for unlimited)</small>
              </div>

              <div className="form-group">
                <label htmlFor="monthlyCap">Monthly Cap</label>
                <input
                  type="number"
                  id="monthlyCap"
                  name="monthlyCap"
                  value={formData.monthlyCap}
                  onChange={handleInputChange}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="field-hint">Maximum clicks per month (0 for unlimited)</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Advertiser Access Control</h2>
            <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '20px' }}>
              Manage which advertisers this affiliate is approved to work with
            </p>

            <div className="form-row">
              <div className="form-group full-width">
                <input
                  type="text"
                  placeholder="Search advertisers..."
                  value={advertiserSearch}
                  onChange={(e) => setAdvertiserSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #5a6c7d',
                    borderRadius: '4px',
                    backgroundColor: '#4a5568',
                    color: '#ecf0f1',
                    fontSize: '14px',
                    marginBottom: '20px'
                  }}
                />
              </div>
            </div>

            {/* Approve All / Block All Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={handleApproveAllAdvertisers}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Approve All ({blockedAdvertisers.length})
              </button>
              <button
                type="button"
                onClick={handleBlockAllAdvertisers}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Block All ({approvedAdvertisers.length})
              </button>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ width: '48%' }}>
                <h3 style={{ color: '#3498db', marginBottom: '10px', fontSize: '16px' }}>
                  Approved For ({approvedAdvertisers.length})
                </h3>
                <div style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  border: '1px solid #5a6c7d',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#3c4855'
                }}>
                  {approvedAdvertisers.length === 0 ? (
                    <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                      {advertiserSearch ? 'No matching advertisers' : 'Not approved for any advertisers'}
                    </div>
                  ) : (
                    approvedAdvertisers.map((advertiser) => (
                      <div key={advertiser.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        borderBottom: '1px solid #5a6c7d',
                        backgroundColor: '#4a5568',
                        marginBottom: '8px',
                        borderRadius: '4px'
                      }}>
                        <div>
                          <div style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>
                            {advertiser.name}
                          </div>
                          <div style={{ color: '#a0aec0', fontSize: '12px' }}>
                            {advertiser.contact_email}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAdvertiserStatus(advertiser.id)}
                          className="btn btn-danger btn-sm"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Block
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="form-group" style={{ width: '48%' }}>
                <h3 style={{ color: '#e74c3c', marginBottom: '10px', fontSize: '16px' }}>
                  Blocked For ({blockedAdvertisers.length})
                </h3>
                <div style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  border: '1px solid #5a6c7d',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#3c4855'
                }}>
                  {blockedAdvertisers.length === 0 ? (
                    <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                      {advertiserSearch ? 'No matching advertisers' : 'Not blocked from any advertisers'}
                    </div>
                  ) : (
                    blockedAdvertisers.map((advertiser) => (
                      <div key={advertiser.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        borderBottom: '1px solid #5a6c7d',
                        backgroundColor: '#4a5568',
                        marginBottom: '8px',
                        borderRadius: '4px'
                      }}>
                        <div>
                          <div style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>
                            {advertiser.name}
                          </div>
                          <div style={{ color: '#a0aec0', fontSize: '12px' }}>
                            {advertiser.contact_email}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAdvertiserStatus(advertiser.id)}
                          className="btn btn-success btn-sm"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Approve
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Affiliate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAffiliate;